import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Zoom,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import NorthIcon from "@mui/icons-material/North";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import masterconfigAxios from "../services/masterconfigAxios";
import {
  MC_CONTROLS_UPLOAD,
  MC_PARAMS_UPLOAD,
  MC_VERSION,
} from "../shared/constants/apiEndpoints";

type UploadKind = "controls" | "params";

interface VersionInfo {
  master_version: string | null;
  master_version_id: string | null;
  master_upload_date: string | null;
  master_description: string;
  controls_version: string | null;
  controls_version_id: string | null;
  controls_upload_date: string | null;
  controls_description: string;
}

interface HistoryRow {
  id: string;
  version: string;
  description: string;
  original_filename: string;
  upload_date: string | null;
  status_label?: string;
  kind: "parameters" | "controls";
}

const formatPublishedDate = (dateString?: string | null) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatFileSize = (bytes?: number) => {
  if (bytes == null || Number.isNaN(bytes)) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const NOTES_MAX = 500;

/** Bump last numeric segment: 31.77 -> 31.78, 1.2.3 -> 1.2.4 */
const getNextVersion = (current?: string | null): string => {
  const raw = (current || "").trim();
  if (!raw) return "1.0";
  const parts = raw.split(".");
  if (parts.length > 0 && parts.every((p) => /^\d+$/.test(p))) {
    const nums = parts.map((p) => parseInt(p, 10));
    nums[nums.length - 1] += 1;
    return nums.join(".");
  }
  return "1.0";
};

const MasterData: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  // Match publish CTA mock (electric indigo / blurple)
  const brand = isDarkMode ? "#818CF8" : "#4F46E5";
  const brandHover = isDarkMode ? "#6366F1" : "#4338CA";
  const publishBtnBg = isDarkMode ? "#6366F1" : "#4F46E5";
  const publishBtnHover = isDarkMode ? "#4F46E5" : "#4338CA";
  const pageBg = isDarkMode ? "#09090B" : "#F5F3FF";
  const cardBg = isDarkMode ? "#18181B" : "#FFFFFF";
  const borderClr = isDarkMode ? "rgba(255,255,255,0.10)" : "#E9E4FF";
  const primaryText = isDarkMode ? "#F4F4F5" : "#1E1B4B";
  const secondaryText = isDarkMode ? "#A1A1AA" : "#6B7280";
  const mutedBg = isDarkMode ? "#27272A" : "#F8F7FC";
  const successBg = isDarkMode ? "rgba(34,197,94,.15)" : "#ECFDF3";
  const successBorder = isDarkMode ? "rgba(34,197,94,.35)" : "#A7F3D0";
  const successText = isDarkMode ? "#86EFAC" : "#059669";
  const cardShadow = isDarkMode
    ? "none"
    : "0 10px 30px rgba(109, 40, 217, 0.06)";

  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);
  const [versionLoading, setVersionLoading] = useState(true);
  const [versionError, setVersionError] = useState<string | null>(null);
  const [activity, setActivity] = useState<HistoryRow[]>([]);
  const [activityLoading, setActivityLoading] = useState(true);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploadType, setUploadType] = useState<UploadKind>("controls");
  const [controlsFile, setControlsFile] = useState<File | null>(null);
  const [paramsFile, setParamsFile] = useState<File | null>(null);
  const [rulesFile, setRulesFile] = useState<File | null>(null);
  const [whatsNew, setWhatsNew] = useState("");
  const [version, setVersion] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const fetchVersion = useCallback(async () => {
    setVersionLoading(true);
    setVersionError(null);
    try {
      const res = await masterconfigAxios.get(MC_VERSION);
      const payload = res.data?.data ?? res.data;
      setVersionInfo({
        master_version: payload?.master_version ?? null,
        master_version_id: payload?.master_version_id ?? null,
        master_upload_date: payload?.master_upload_date ?? null,
        master_description: payload?.master_description ?? "",
        controls_version: payload?.controls_version ?? null,
        controls_version_id: payload?.controls_version_id ?? null,
        controls_upload_date: payload?.controls_upload_date ?? null,
        controls_description: payload?.controls_description ?? "",
      });
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Unable to load current versions";
      setVersionError(msg);
      setVersionInfo(null);
    } finally {
      setVersionLoading(false);
    }
  }, []);

  const fetchActivity = useCallback(async () => {
    setActivityLoading(true);
    try {
      const res = await masterconfigAxios.get(MC_VERSION, {
        params: { history: "all" },
      });
      const payload = res.data?.data ?? res.data;
      const params = Array.isArray(payload?.history?.parameters)
        ? payload.history.parameters.map((r: any) => ({
            ...r,
            kind: "parameters" as const,
          }))
        : [];
      const controls = Array.isArray(payload?.history?.controls)
        ? payload.history.controls.map((r: any) => ({
            ...r,
            kind: "controls" as const,
          }))
        : [];
      const merged = [...params, ...controls].sort((a, b) => {
        const ta = a.upload_date ? new Date(a.upload_date).getTime() : 0;
        const tb = b.upload_date ? new Date(b.upload_date).getTime() : 0;
        return tb - ta;
      });
      setActivity(merged.slice(0, 4));
    } catch {
      setActivity([]);
    } finally {
      setActivityLoading(false);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchVersion(), fetchActivity()]);
  }, [fetchVersion, fetchActivity]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const copyId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      toast.success("Version ID copied");
    } catch {
      toast.error("Unable to copy");
    }
  };

  const onDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    if (uploadType === "controls") {
      setControlsFile(acceptedFiles[0]);
    } else {
      setParamsFile(acceptedFiles[0]);
    }
  };

  const { open: openFileBrowser, getRootProps, getInputProps, isDragActive } =
    useDropzone({
      onDrop,
      noClick: true,
      multiple: false,
      accept: {
        "text/csv": [".csv"],
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
          ".xlsx",
        ],
        "application/vnd.ms-excel": [".xls"],
      },
    });

  const onRulesDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) setRulesFile(acceptedFiles[0]);
  };

  const {
    getRootProps: getRulesRootProps,
    getInputProps: getRulesInputProps,
    isDragActive: isRulesDragActive,
    open: openRulesBrowser,
  } = useDropzone({
    onDrop: onRulesDrop,
    noClick: true,
    multiple: false,
    accept: { "application/json": [".json"] },
  });

  const currentVersionFor = useCallback(
    (kind: UploadKind) =>
      kind === "controls"
        ? versionInfo?.controls_version
        : versionInfo?.master_version,
    [versionInfo]
  );

  const openPublish = async (kind: UploadKind = "controls") => {
    setUploadType(kind);
    setControlsFile(null);
    setParamsFile(null);
    setRulesFile(null);
    setWhatsNew("");
    setVersion(getNextVersion(currentVersionFor(kind)));
    setIsModalOpen(true);
    try {
      const res = await masterconfigAxios.get(MC_VERSION);
      const payload = res.data?.data ?? res.data;
      const info: VersionInfo = {
        master_version: payload?.master_version ?? null,
        master_version_id: payload?.master_version_id ?? null,
        master_upload_date: payload?.master_upload_date ?? null,
        master_description: payload?.master_description ?? "",
        controls_version: payload?.controls_version ?? null,
        controls_version_id: payload?.controls_version_id ?? null,
        controls_upload_date: payload?.controls_upload_date ?? null,
        controls_description: payload?.controls_description ?? "",
      };
      setVersionInfo(info);
      const current =
        kind === "controls" ? info.controls_version : info.master_version;
      setVersion(getNextVersion(current));
    } catch {
      // keep optimistic next version from cached data
    }
  };

  const handleCloseModal = () => {
    if (isSaving) return;
    setIsModalOpen(false);
    setControlsFile(null);
    setParamsFile(null);
    setRulesFile(null);
    setWhatsNew("");
    setVersion("");
  };

  const handleSave = async () => {
    if (!version.trim()) {
      toast.error("Please enter a version.");
      return;
    }
    if (!whatsNew.trim()) {
      toast.error("Please enter release notes.");
      return;
    }
    if (uploadType === "controls" && !controlsFile) {
      toast.error("Please select a controls file.");
      return;
    }
    if (uploadType === "params") {
      if (!paramsFile) {
        toast.error("Please select a parameters file.");
        return;
      }
      if (!rulesFile) {
        toast.error("Please select a rules JSON file.");
        return;
      }
    }

    setIsSaving(true);
    try {
      if (uploadType === "controls") {
        const formData = new FormData();
        formData.append("controls", controlsFile!);
        formData.append("version", version);
        formData.append("description", whatsNew);
        const response = await masterconfigAxios.post(
          MC_CONTROLS_UPLOAD,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        toast.success(response?.data?.message || "Controls published successfully.");
      } else {
        const formData = new FormData();
        formData.append("params", paramsFile!);
        formData.append("rules", rulesFile!);
        formData.append("version", version);
        formData.append("description", whatsNew);
        const response = await masterconfigAxios.post(
          MC_PARAMS_UPLOAD,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        toast.success(
          response?.data?.message || "Parameters published successfully."
        );
      }
      handleCloseModal();
      refreshAll();
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Publish failed. Please try again.";
      toast.error(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const versionCards = [
    {
      title: "Parameters",
      subtitle: "Active master version",
      version: versionInfo?.master_version,
      id: versionInfo?.master_version_id,
      historyType: "parameters" as const,
      icon: (
        <DescriptionOutlinedIcon sx={{ fontSize: 18, color: brand }} />
      ),
      iconBg: isDarkMode ? "rgba(129,140,248,.18)" : "#EDE9FE",
    },
    {
      title: "Controls",
      subtitle: "Active controls version",
      version: versionInfo?.controls_version,
      id: versionInfo?.controls_version_id,
      historyType: "controls" as const,
      icon: (
        <CategoryOutlinedIcon sx={{ fontSize: 18, color: successText }} />
      ),
      iconBg: isDarkMode ? "rgba(34,197,94,.15)" : "#ECFDF3",
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        height: "calc(100vh - 103px)",
        display: "flex",
        flexDirection: "column",
        gap: 2.5,
        p: { xs: 1.5, md: 2.5 },
        borderRadius: 3,
        bgcolor: pageBg,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: brand,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              boxShadow: "0 8px 20px rgba(109, 40, 217, 0.28)",
            }}
          >
            <LayersOutlinedIcon sx={{ color: "#fff", fontSize: 24 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{ fontWeight: 700, fontSize: 22, color: primaryText, lineHeight: 1.2 }}
            >
              Master Data
            </Typography>
            <Typography sx={{ fontSize: 13, color: secondaryText, mt: 0.4 }}>
              Manage and publish master data versions for parameters and controls.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1.25, flexShrink: 0 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refreshAll}
            disabled={versionLoading}
            sx={{
              textTransform: "none",
              borderColor: borderClr,
              color: primaryText,
              bgcolor: cardBg,
              borderRadius: 2.5,
              px: 2,
              fontWeight: 500,
              "&:hover": { borderColor: borderClr, bgcolor: mutedBg },
            }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            disableElevation
            startIcon={<NorthIcon sx={{ fontSize: 16 }} />}
            onClick={() => openPublish("params")}
            sx={{
              textTransform: "none",
              bgcolor: `${publishBtnBg} !important`,
              backgroundColor: `${publishBtnBg} !important`,
              color: "#fff !important",
              "&:hover": {
                bgcolor: `${publishBtnHover} !important`,
                backgroundColor: `${publishBtnHover} !important`,
              },
              borderRadius: 2.5,
              px: 2.25,
              fontWeight: 600,
              boxShadow: "0 8px 18px rgba(79, 70, 229, 0.35)",
            }}
          >
            Publish new version
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        {versionLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 10,
            }}
          >
            <CircularProgress size={32} sx={{ color: brand }} />
          </Box>
        ) : versionError ? (
          <Alert
            severity="error"
            sx={{ borderRadius: 2, maxWidth: 560 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={fetchVersion}
                sx={{ textTransform: "none" }}
              >
                Retry
              </Button>
            }
          >
            {versionError}
          </Alert>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 2.5,
            }}
          >
            {versionCards.map((card) => (
              <Paper
                key={card.title}
                elevation={0}
                sx={{
                  p: 2.75,
                  borderRadius: 3,
                  bgcolor: cardBg,
                  border: `1px solid ${borderClr}`,
                  boxShadow: cardShadow,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.25,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        bgcolor: card.iconBg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {card.icon}
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 15,
                          fontWeight: 700,
                          color: primaryText,
                          lineHeight: 1.2,
                        }}
                      >
                        {card.title}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: secondaryText }}>
                        {card.subtitle}
                      </Typography>
                    </Box>
                  </Box>
                  {card.version && (
                    <Chip
                      size="small"
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                          }}
                        >
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              bgcolor: successText,
                            }}
                          />
                          Active
                        </Box>
                      }
                      sx={{
                        height: 26,
                        fontSize: 12,
                        fontWeight: 600,
                        bgcolor: successBg,
                        color: successText,
                        border: `1px solid ${successBorder}`,
                        borderRadius: 999,
                        "& .MuiChip-label": { px: 1.1 },
                      }}
                    />
                  )}
                </Box>

                <Typography
                  sx={{
                    fontSize: 40,
                    fontWeight: 800,
                    color: primaryText,
                    lineHeight: 1,
                    letterSpacing: "-0.03em",
                  }}
                >
                  {card.version ? `v${card.version}` : "—"}
                </Typography>

                {card.id ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.5,
                      py: 1.15,
                      borderRadius: 2,
                      bgcolor: mutedBg,
                      border: `1px solid ${borderClr}`,
                    }}
                  >
                    <Typography
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: 12,
                        fontFamily: "'Fira Code', monospace",
                        color: primaryText,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {card.id}
                    </Typography>
                    <Tooltip title="Copy version ID">
                      <IconButton
                        size="small"
                        onClick={() => copyId(card.id!)}
                        sx={{ color: secondaryText }}
                      >
                        <ContentCopyIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: 13, color: secondaryText }}>
                    No active version published yet.
                  </Typography>
                )}

                  <Box
                    component="button"
                    type="button"
                    onClick={() =>
                      navigate(`/master-data/history?type=${card.historyType}`)
                    }
                    sx={{
                      alignSelf: "flex-start",
                      border: "none",
                      background: "none",
                      p: 0,
                      mt: "auto",
                      pt: 0.5,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.75,
                      fontSize: 13,
                      fontWeight: 600,
                      color: brand,
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    <HistoryOutlinedIcon sx={{ fontSize: 16 }} />
                    View version history →
                  </Box>
              </Paper>
            ))}
          </Box>
        )}

        {/* Recent activity — timeline */}
        <Paper
          elevation={0}
          sx={{
            p: 2.75,
            borderRadius: 3,
            bgcolor: cardBg,
            border: `1px solid ${borderClr}`,
            boxShadow: cardShadow,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, mb: 2 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                bgcolor: isDarkMode ? "rgba(129,140,248,.18)" : "#EDE9FE",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <HistoryOutlinedIcon sx={{ fontSize: 18, color: brand }} />
            </Box>
            <Box>
              <Typography
                sx={{ fontWeight: 700, fontSize: 15, color: primaryText }}
              >
                Recent activity
              </Typography>
              <Typography sx={{ fontSize: 13, color: secondaryText, mt: 0.25 }}>
                Latest publishes appear here with who, when and what changed.
              </Typography>
            </Box>
          </Box>

          {activityLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={22} sx={{ color: brand }} />
            </Box>
          ) : activity.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: secondaryText, pl: 5.5 }}>
              No publishes yet.
            </Typography>
          ) : (
            <Box sx={{ position: "relative", pl: 1.5 }}>
              {activity.map((row, index) => {
                const isControls = row.kind === "controls";
                const msg = row.description?.trim() || "—";
                return (
                  <Box
                    key={`${row.kind}-${row.id}`}
                    sx={{
                      display: "flex",
                      gap: 1.75,
                      position: "relative",
                      pb: index < activity.length - 1 ? 2.5 : 0,
                    }}
                  >
                    {/* timeline line */}
                    {index < activity.length - 1 && (
                      <Box
                        sx={{
                          position: "absolute",
                          left: 15,
                          top: 28,
                          bottom: 0,
                          width: 2,
                          bgcolor: isDarkMode
                            ? "rgba(129,140,248,0.25)"
                            : "#DDD6FE",
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        bgcolor: isControls
                          ? successBg
                          : isDarkMode
                            ? "rgba(129,140,248,.18)"
                            : "#EDE9FE",
                        border: `2px solid ${cardBg}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        zIndex: 1,
                      }}
                    >
                      {isControls ? (
                        <CategoryOutlinedIcon
                          sx={{ fontSize: 15, color: successText }}
                        />
                      ) : (
                        <DescriptionOutlinedIcon
                          sx={{ fontSize: 15, color: brand }}
                        />
                      )}
                    </Box>

                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 2,
                        flexWrap: "wrap",
                        pt: 0.25,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 700,
                              color: primaryText,
                            }}
                          >
                            {isControls ? "Controls" : "Parameters"}
                          </Typography>
                          <Chip
                            size="small"
                            label={`v${row.version}`}
                            sx={{
                              height: 22,
                              fontSize: 11,
                              fontWeight: 700,
                              fontFamily: "'Fira Code', monospace",
                              bgcolor: isDarkMode
                                ? "rgba(129,140,248,.18)"
                                : "#EDE9FE",
                              color: brand,
                              borderRadius: 999,
                            }}
                          />
                        </Box>
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: secondaryText,
                            mt: 0.4,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: 480,
                          }}
                        >
                          {msg}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.75,
                          color: secondaryText,
                          flexShrink: 0,
                        }}
                      >
                        <CalendarTodayOutlinedIcon sx={{ fontSize: 14 }} />
                        <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
                          {formatPublishedDate(row.upload_date) || "—"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Paper>
      </Box>

      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Zoom}
        TransitionProps={{ timeout: 350 } as any}
        PaperProps={{
          sx: {
            borderRadius: "20px",
            p: 0.5,
            backgroundColor: cardBg,
            color: primaryText,
            boxShadow: "0 24px 60px rgba(15, 23, 42, 0.28)",
            // Same fixed size for Controls and Parameters
            width: "100%",
            maxWidth: 520,
            height: 640,
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        {(() => {
          const isControls = uploadType === "controls";
          const accent = isControls ? successText : brand;
          const accentSoft = isControls
            ? successBg
            : isDarkMode
              ? "rgba(129,140,248,.16)"
              : "#EEF2FF";
          const accentBorder = isControls ? successBorder : borderClr;
          const currentVer = isControls
            ? versionInfo?.controls_version
            : versionInfo?.master_version;
          const mainFile = isControls ? controlsFile : paramsFile;
          const kindLabel = isControls ? "controls" : "parameters";

          return (
            <>
              <DialogTitle
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 2,
                  pt: 2.25,
                  px: 2.5,
                  pb: 1.5,
                  flexShrink: 0,
                }}
              >
                <Box sx={{ display: "flex", gap: 1.5, minWidth: 0, flex: 1 }}>
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: 2,
                      bgcolor: accentSoft,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {isControls ? (
                      <CategoryOutlinedIcon sx={{ color: accent }} />
                    ) : (
                      <DescriptionOutlinedIcon sx={{ color: accent }} />
                    )}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{ fontWeight: 800, fontSize: 18, color: primaryText }}
                    >
                      Publish new version
                    </Typography>
                    <Typography
                      sx={{ fontSize: 13, color: secondaryText, mt: 0.35 }}
                    >
                      Upload files for a new release of your {kindLabel}.
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    flexShrink: 0,
                    mt: 0.25,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      color:
                        uploadType === "controls" ? primaryText : secondaryText,
                    }}
                  >
                    Controls
                  </Typography>
                  <Switch
                    checked={uploadType === "params"}
                    onChange={(_, checked) => {
                      const nextKind: UploadKind = checked ? "params" : "controls";
                      setUploadType(nextKind);
                      setControlsFile(null);
                      setParamsFile(null);
                      setRulesFile(null);
                      setVersion(getNextVersion(currentVersionFor(nextKind)));
                    }}
                    disabled={isSaving}
                    size="small"
                    sx={{
                      width: 40,
                      height: 22,
                      padding: 0,
                      "& .MuiSwitch-switchBase": {
                        padding: "2px",
                        "&.Mui-checked": {
                          transform: "translateX(18px)",
                          color: "#fff",
                          "& + .MuiSwitch-track": {
                            backgroundColor: brand,
                            opacity: 1,
                          },
                        },
                      },
                      "& .MuiSwitch-thumb": {
                        width: 18,
                        height: 18,
                        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                      },
                      "& .MuiSwitch-track": {
                        borderRadius: 11,
                        backgroundColor: isDarkMode ? "#3F3F46" : "#D1D5DB",
                        opacity: 1,
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 600,
                      color:
                        uploadType === "params" ? primaryText : secondaryText,
                    }}
                  >
                    Parameters
                  </Typography>
                  <IconButton
                    onClick={handleCloseModal}
                    size="small"
                    disabled={isSaving}
                    sx={{ color: secondaryText, ml: 0.25 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </DialogTitle>

              <DialogContent
                sx={{
                  px: 2.5,
                  pb: 1,
                  pt: 0.5,
                  flex: 1,
                  overflowY: "auto",
                  minHeight: 0,
                }}
              >
                {/* Current version */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    mb: 2.25,
                    px: 1.75,
                    py: 1.35,
                    borderRadius: 2.5,
                    bgcolor: accentSoft,
                    border: `1px solid ${accentBorder}`,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                    <Inventory2OutlinedIcon sx={{ color: accent, fontSize: 22 }} />
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: primaryText,
                        }}
                      >
                        Current {kindLabel} version
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: secondaryText }}>
                        This is the currently active version.
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    size="small"
                    label={currentVer ? `v${currentVer}` : "—"}
                    sx={{
                      fontFamily: "'Fira Code', monospace",
                      fontWeight: 700,
                      bgcolor: cardBg,
                      color: accent,
                      border: `1px solid ${accentBorder}`,
                    }}
                  />
                </Box>

                {/* Main file */}
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: primaryText,
                      mb: 1,
                    }}
                  >
                    {isControls ? "Controls" : "Parameters"} file{" "}
                    <Box component="span" sx={{ color: "#EF4444" }}>
                      *
                    </Box>
                  </Typography>
                  <Paper
                    {...getRootProps()}
                    variant="outlined"
                    sx={{
                      p: 2.25,
                      borderStyle: "dashed",
                      borderWidth: 1.5,
                      borderColor: isDragActive ? accent : borderClr,
                      bgcolor: isDragActive ? accentSoft : mutedBg,
                      textAlign: "center",
                      cursor: "pointer",
                      borderRadius: 2.5,
                    }}
                    onClick={openFileBrowser}
                  >
                    <input {...getInputProps()} />
                    <CloudUploadIcon sx={{ color: accent, mb: 0.75, fontSize: 28 }} />
                    <Typography sx={{ fontSize: 13, color: secondaryText }}>
                      Drag and drop your {kindLabel} file here, or{" "}
                      <Box
                        component="span"
                        sx={{ color: brand, fontWeight: 700 }}
                      >
                        browse files
                      </Box>
                    </Typography>
                    <Typography
                      sx={{ fontSize: 12, color: secondaryText, mt: 0.5 }}
                    >
                      Excel (.xlsx) or CSV
                    </Typography>
                  </Paper>
                  {mainFile && (
                    <Box
                      sx={{
                        mt: 1.25,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        px: 1.5,
                        py: 1.1,
                        borderRadius: 2,
                        bgcolor: mutedBg,
                        border: `1px solid ${borderClr}`,
                      }}
                    >
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: 1.5,
                          bgcolor: isDarkMode
                            ? "rgba(34,197,94,.15)"
                            : "#ECFDF3",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: successText,
                          flexShrink: 0,
                        }}
                      >
                        <InsertDriveFileOutlinedIcon sx={{ fontSize: 18 }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: primaryText,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {mainFile.name}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: secondaryText }}>
                          {formatFileSize(mainFile.size)}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        disabled={isSaving}
                        onClick={() =>
                          isControls
                            ? setControlsFile(null)
                            : setParamsFile(null)
                        }
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>

                {/* Rules — parameters only (not for controls) */}
                {uploadType === "params" && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: primaryText,
                        mb: 1,
                      }}
                    >
                      Rules file{" "}
                      <Box component="span" sx={{ color: "#EF4444" }}>
                        *
                      </Box>
                    </Typography>
                    <Paper
                      {...getRulesRootProps()}
                      variant="outlined"
                      sx={{
                        p: 2.25,
                        borderStyle: "dashed",
                        borderWidth: 1.5,
                        borderColor: isRulesDragActive ? brand : borderClr,
                        bgcolor: isRulesDragActive
                          ? isDarkMode
                            ? "rgba(129,140,248,0.08)"
                            : "#F5F3FF"
                          : mutedBg,
                        textAlign: "center",
                        cursor: "pointer",
                        borderRadius: 2.5,
                      }}
                      onClick={openRulesBrowser}
                    >
                      <input {...getRulesInputProps()} />
                      <InsertDriveFileOutlinedIcon
                        sx={{ color: brand, mb: 0.75, fontSize: 28 }}
                      />
                      <Typography sx={{ fontSize: 13, color: secondaryText }}>
                        Drag and drop rules file here, or{" "}
                        <Box
                          component="span"
                          sx={{ color: brand, fontWeight: 700 }}
                        >
                          browse files
                        </Box>
                      </Typography>
                      <Typography
                        sx={{ fontSize: 12, color: secondaryText, mt: 0.5 }}
                      >
                        JSON (.json)
                      </Typography>
                    </Paper>
                    {rulesFile && (
                      <Box
                        sx={{
                          mt: 1.25,
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                          px: 1.5,
                          py: 1.1,
                          borderRadius: 2,
                          bgcolor: mutedBg,
                          border: `1px solid ${borderClr}`,
                        }}
                      >
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: 1.5,
                            bgcolor: isDarkMode
                              ? "rgba(129,140,248,.18)"
                              : "#EDE9FE",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: brand,
                            flexShrink: 0,
                          }}
                        >
                          <InsertDriveFileOutlinedIcon sx={{ fontSize: 18 }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: primaryText,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {rulesFile.name}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: secondaryText }}>
                            {formatFileSize(rulesFile.size)}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          disabled={isSaving}
                          onClick={() => setRulesFile(null)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Version (required by API) */}
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: primaryText,
                      mb: 1,
                    }}
                  >
                    Version{" "}
                    <Box component="span" sx={{ color: "#EF4444" }}>
                      *
                    </Box>
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    placeholder="e.g. 31.78"
                  />
                </Box>

                {/* Release notes required */}
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: primaryText,
                      }}
                    >
                      Release notes{" "}
                      <Box component="span" sx={{ color: "#EF4444" }}>
                        *
                      </Box>
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: secondaryText }}>
                      {whatsNew.length} / {NOTES_MAX}
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    size="small"
                    value={whatsNew}
                    onChange={(e) =>
                      setWhatsNew(e.target.value.slice(0, NOTES_MAX))
                    }
                    multiline
                    minRows={3}
                    placeholder="Summarize what changed in this version..."
                  />
                </Box>
              </DialogContent>

              <DialogActions
                sx={{
                  px: 2.5,
                  pb: 2.25,
                  pt: 1.5,
                  gap: 1,
                  flexShrink: 0,
                  borderTop: `1px solid ${borderClr}`,
                }}
              >
                <Button
                  onClick={handleCloseModal}
                  disabled={isSaving}
                  sx={{
                    textTransform: "none",
                    color: brand,
                    fontWeight: 600,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  disableElevation
                  onClick={handleSave}
                  disabled={isSaving}
                  sx={{
                    textTransform: "none",
                    bgcolor: `${publishBtnBg} !important`,
                    backgroundColor: `${publishBtnBg} !important`,
                    color: "#fff !important",
                    borderRadius: 999,
                    px: 0.75,
                    py: 0.6,
                    minHeight: 44,
                    fontWeight: 700,
                    boxShadow: "0 10px 24px rgba(79, 70, 229, 0.35)",
                    "&:hover": {
                      bgcolor: `${publishBtnHover} !important`,
                      backgroundColor: `${publishBtnHover} !important`,
                    },
                    "&.Mui-disabled": {
                      bgcolor: `${publishBtnBg} !important`,
                      color: "#fff !important",
                      opacity: 0.7,
                    },
                  }}
                >
                  {isSaving ? (
                    <Box sx={{ px: 3, display: "flex", alignItems: "center" }}>
                      <CircularProgress size={20} sx={{ color: "#fff" }} />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        pl: 0.5,
                        pr: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          bgcolor: "rgba(255,255,255,0.18)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <SendOutlinedIcon sx={{ fontSize: 16, color: "#fff" }} />
                      </Box>
                      <Box
                        sx={{
                          width: "1px",
                          alignSelf: "stretch",
                          bgcolor: "rgba(255,255,255,0.28)",
                          my: 0.5,
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: "#fff",
                          px: 0.5,
                          textTransform: "none",
                        }}
                      >
                        Publish {kindLabel === "controls" ? "Controls" : "Parameters"}
                      </Typography>
                      <ArrowForwardIcon sx={{ fontSize: 18, color: "#fff" }} />
                    </Box>
                  )}
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
    </Box>
  );
};

export default MasterData;
